/**
 * The live site state the operations screen draws.
 *
 * One module owns the polling so a screenful of panels makes one set of
 * requests rather than one each, and so every panel agrees about what time it
 * is. The shape it returns is the shape the screens already consume.
 *
 * Where the backend has no answer, this returns the console's own unknown
 * marker rather than a plausible number. A fabricated siren state is worse
 * than an empty one.
 */
import { createContext, useContext, useEffect, useMemo } from "react";

import { path, usePolled } from "./api.js";
import { personnelLiveMapRowOf } from "./response-live.js";
import { html } from "./ui.js";
import { formatUserInstant, instantMs } from "./time.js";
import { boardHeadingOf, boardLabelSay } from "./operator-say.js";

/** What the screens render when the server cannot answer. */
export const UNKNOWN = "—";

/** Places / Machine writes fire this so Ops map fences refresh immediately. */
export const LIVE_ZONES_CHANGED = "croc-zones-changed";

/** Bump shared Live zones (+ device membership) without waiting for the poll. */
export function bumpLiveZones(detail) {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(LIVE_ZONES_CHANGED, { detail: detail || {} }));
  } catch {
    /* */
  }
}

/* Cadences. The device register is the expensive read, so it goes slowest and
   uses the cached path; four API replicas share one Redis TTL behind it.     */
const ALARMS_MS = 20000;
const EVENTS_MS = 15000;
const INCIDENTS_MS = 30000;
const DEVICES_MS = 45000;
/* People move often. Zones must stay near-real with Places survey writes:
   poll every 10s for other seats, and bumpLiveZones on local mutate.        */
const PEOPLE_MS = 20000;
const ZONES_MS = 10000;
const SOFT_MS = 30000;

const ALARMS_PATH = path("get__alarms", { query: { since_hours: 24, limit: 100 } });
const EVENTS_PATH = path("get__events", { query: { limit: 40 } });
const INCIDENTS_PATH = path("get__incidents", { query: { limit: 5 } });
const DEVICES_PATH = path("get__devices");
const PEOPLE_PATH = path("get__personnel");
const ZONES_PATH = path("get__zones", { query: { all_sites: 1 } });
const SOFT_PATH = path("get__ai_soft-lanes");

/** `2026-08-01T03:18:52` → `03:18:52`. Server times are UTC ISO; sites read clocks. */
function clockOf(iso) {
  return iso ? formatUserInstant(iso, { date: false }) : UNKNOWN;
}

function secondsSince(iso) {
  const ms = instantMs(iso);
  return Number.isFinite(ms) ? (Date.now() - ms) / 1000 : Infinity;
}

/**
 * Alarms have no lifecycle in the store — no acknowledge, no assignee, no
 * resolved flag. "Open" here is a recency window and nothing more, matching
 * the fifteen minutes the map already uses to decide a pin is pulsing.
 */
const OPEN_WINDOW_S = 15 * 60;

/**
 * Guess a glyph family from what the server already tells us. The list
 * response has no dedicated kind column, and inventing one from thin air
 * would lie. A label or hardware code that clearly names a camera, siren,
 * door or mast is enough to pick an icon; anything else is just a node.
 */
function guessKind(row) {
  const blob = [
    row.display_label, row.map_label, row.hw_code_resolved,
    row.product_hw_code, row.board_profile, row.chip_target,
  ].filter(Boolean).join(" ").toLowerCase();
  if (/\b(cam|camera|snap|vision|lidar)\b/.test(blob)) return /lidar/.test(blob) ? "lidar" : "camera";
  if (/\b(siren|strobe|horn|alarm)\b/.test(blob)) return "siren";
  if (/\b(door|contact|reed|magnet)\b/.test(blob)) return "contact";
  if (/\b(mast|gateway|bridge|hub)\b/.test(blob)) return "gateway";
  if (/\b(sensor|leak|smoke|pir|temp)\b/.test(blob)) return "sensor";
  return "node";
}

/**
 * Flatten a `GET /devices` item into the shape every screen already draws.
 * Coordinates stay `[lng, lat]` to match MapLibre. A device with no surveyed
 * position keeps `ll: null` rather than a zero, so the map can skip it
 * without inventing a pin in the Atlantic.
 */
function toDevices(payload) {
  const rows = (payload && payload.items) || [];
  const mapped = rows.map((r) => {
    const preview = r.status_preview || {};
    const lat = r.map_lat == null ? null : Number(r.map_lat);
    const lng = r.map_lng == null ? null : Number(r.map_lng);
    const hasPos = Number.isFinite(lat) && Number.isFinite(lng);
    const online = !!r.is_online;
    const age = Number.isFinite(Number(r.last_signal_age_s)) ? Number(r.last_signal_age_s) : -1;
    const label = boardLabelSay(r, r.device_id);
    const claimed = r.claimed_capabilities || r.claimed_caps || r.claimed;
    const verified = r.verified_capabilities || r.verified_caps || r.verified;
    return {
      id: String(r.device_id || "").trim(),
      label,
      kind: guessKind(r),
      ll: hasPos ? [lng, lat] : null,
      zone: String(r.zone || "").trim(),
      site: String(r.site_id || r.site || "").trim(),
      group: String(r.notification_group || "").trim(),
      is_shared: !!r.is_shared,
      shared_by: String(r.shared_by || "").trim(),
      rssi: preview.rssi == null ? null : Number(preview.rssi),
      vbat: preview.vbat == null ? null : Number(preview.vbat),
      age,
      fw: String(r.fw || "").trim(),
      offline: !online,
      off: online ? "" : String(preview.disconnect_reason || "").trim(),
      pending: Number(r.pending_cmds || 0) > 0 ? Number(r.pending_cmds) : 0,
      pulse: String(r.map_pulse || (online ? "online" : "offline")),
      online,
      hw_code: String(r.hw_code_resolved || r.product_hw_code || "").trim(),
      board_profile: String(r.board_profile || "").trim(),
      chip_target: String(r.chip_target || "").trim(),
      device_type: String(r.device_type || "").trim(),
      claimedCaps: Array.isArray(claimed) ? claimed.map(String) : [],
      verifiedCaps: Array.isArray(verified) ? verified.map(String) : [],
    };
  }).filter((d) => d.id);
  /* One board · one row. Duplicate device_id (two camera names) stays one unit. */
  const seen = new Set();
  const unique = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    unique.push(d);
  }
  return unique;
}

/** A centre the map can open on when the site has surveyed positions. */
function centreOf(devices) {
  const pts = devices.map((d) => d.ll).filter(Boolean);
  if (!pts.length) return null;
  return [
    pts.reduce((a, p) => a + p[0], 0) / pts.length,
    pts.reduce((a, p) => a + p[1], 0) / pts.length,
  ];
}

function toAlarms(payload, labelOf) {
  const rows = (payload && payload.items) || [];
  return rows.map((a) => ({
    id: `ALM-${a.id}`,
    dev: a.source_id || "",
    src: labelOf(a.source_id) || boardHeadingOf(a.source_id) || UNKNOWN,
    zone: a.zone || "",
    by: a.triggered_by || "",
    ts: clockOf(a.created_at),
    open: secondsSince(a.created_at) < OPEN_WINDOW_S,
    // No severity column exists. Nothing is marked critical rather than
    // guessing one from fan-out, which counts recipients, not danger.
    crit: false,
    fanout: Number(a.fanout_count || 0),
    notified: Number(a.email_sent || 0) > 0,
    at: a.created_at || "",
  }));
}

function toEvents(payload) {
  const rows = (payload && payload.items) || [];
  return rows.map((e) => ({
    ts: clockOf(e.ts || e.ts_malaysia),
    lvl: e.level || "info",
    txt: e.summary || e.event_type || "",
    id: e.id,
    /* SHELL-1: typed notification deep-link fields (owning object). */
    device: String(e.device_id || e.source_id || "").trim(),
    incident: String(e.incident_id || "").trim(),
    event_type: String(e.event_type || e.category || "").trim(),
  }));
}

/**
 * The belief callout. Incidents are the runtime's own correlation record, so
 * the newest open one is what the site currently believes. A tenant with no
 * incidents believes nothing, and the callout does not render.
 */
function toBelief(payload, labelOf) {
  const row = ((payload && payload.items) || [])[0];
  if (!row) return null;
  const conf = row.confidence != null ? row.confidence
    : row.score != null ? row.score
    : null;
  return {
    dev: row.device_id || "",
    label: row.title || row.kind || row.incident_type || "Incident",
    conf: conf == null ? UNKNOWN : Math.round(Number(conf) <= 1 ? Number(conf) * 100 : Number(conf)),
    since: clockOf(row.opened_at || row.created_at),
    rules: row.rule_id || row.rule || UNKNOWN,
    src: row.source || (labelOf(row.device_id) ? `Rules + ${labelOf(row.device_id)}` : "Rules"),
    id: row.incident_id || row.id,
  };
}

/**
 * What the site has already done. Notify is real — the alarm row records the
 * fan-out. Siren is not: arming is a command, and no read reports whether a
 * relay is currently closed, so it stays unknown instead of "not armed".
 */
function toOps(alarms) {
  const notified = alarms.find((a) => a.notified);
  return {
    notify: notified ? `${notified.ts} · ${notified.fanout || 1} recipients` : UNKNOWN,
    siren: UNKNOWN,
    sirenTone: "",
  };
}

/**
 * The responder roster.
 *
 * Two fields the old fixture carried are gone rather than approximated:
 * handset battery, which nothing reports, and ETA, which would require routing
 * the server does not do — it ranks responders by straight-line distance.
 *
 * Register profile labels come from Live GET /personnel when the server arms
 * them — never guessed from username shape.
 */
function toPeople(payload) {
  const rows = (payload && payload.items) || [];
  return rows.map((p) => {
    const labels = personnelLiveMapRowOf(p) || {
      id: String(p.username || ""),
      name: String(p.username || ""),
      callsign: "",
      site: "",
      crew: "",
      registerProfileLive: false,
    };
    const pos = p.position || null;
    const asg = p.assignment || null;
    return {
      id: labels.id,
      name: labels.name,
      callsign: labels.callsign || "",
      site: labels.site || "",
      crew: labels.crew || "",
      registerProfileLive: labels.registerProfileLive === true,
      role: String(p.role || ""),
      phone: String(p.phone || ""),
      avatar: p.avatar_url || null,
      ll: pos && pos.lng != null && pos.lat != null ? [Number(pos.lng), Number(pos.lat)] : null,
      acc: pos && pos.accuracy_m != null ? Number(pos.accuracy_m) : null,
      age: pos && pos.age_s != null ? Number(pos.age_s) : null,
      stale: pos ? !!pos.stale : null,
      // `null` means the server could not answer, which is not the same as off.
      onCall: p.on_call == null ? null : !!p.on_call,
      consent: String(p.consent || "none"),
      state: asg ? String(asg.status || "") : pos ? "idle" : "invisible",
      disp: asg ? asg.dispatch_id : null,
      dispDevice: asg ? asg.device_id : null,
    };
  }).filter((p) => p.id);
}

/**
 * Boundaries, keyed by label for the map layer that draws them.
 *
 * The rings come from surveyed geometry, and so does membership: the server
 * decides which devices are inside a boundary by testing their coordinates
 * against the ring, not by comparing the boundary's name to a free-text field
 * on the device row. `mismatch` carries the devices whose typed label
 * disagrees with where they physically are, which is a data-quality signal
 * worth showing rather than quietly resolving one way or the other.
 */
function toZones(payload) {
  const rows = (payload && payload.items) || [];
  const rings = {};
  const byLabel = new Map();
  for (const z of rows) {
    const label = String(z.label || z.zone_id || "").trim();
    const siteId = String(z.site_id || "").trim();
    if (!label) continue;
    const ringKey = byLabel.has(label) && siteId ? label + " · " + siteId : label;
    if (Array.isArray(z.ring) && z.ring.length >= 3) rings[ringKey] = z.ring;
    byLabel.set(ringKey, {
      id: z.zone_id,
      label: ringKey,
      siteId,
      kind: String(z.kind || ""),
      ring: z.ring || null,
      bbox: z.bbox || null,
      deviceIds: z.device_ids || [],
      count: Number(z.device_count || 0),
      mismatch: z.label_mismatch_device_ids || [],
    });
  }
  return { rings, list: [...byLabel.values()] };
}

const Ctx = createContext(null);

/**
 * Owns the four polls the console shares. Device labels are resolved from the
 * register itself, so the alarms and belief panels name a device the same way
 * the map does, without a second read.
 */
export function LiveProvider({ children, armed = true }) {
  const alarmsR = usePolled(armed ? ALARMS_PATH : null, ALARMS_MS);
  const eventsR = usePolled(armed ? EVENTS_PATH : null, EVENTS_MS);
  const incidentsR = usePolled(armed ? INCIDENTS_PATH : null, INCIDENTS_MS);
  const devicesR = usePolled(armed ? DEVICES_PATH : null, DEVICES_MS);
  const peopleR = usePolled(armed ? PEOPLE_PATH : null, PEOPLE_MS);
  const zonesR = usePolled(armed ? ZONES_PATH : null, ZONES_MS);
  const softR = usePolled(armed ? SOFT_PATH : null, SOFT_MS);

  /* Same-tab Places / Inks / Machine writes → Ops fences without waiting 10s. */
  useEffect(() => {
    if (!armed) return undefined;
    const onBump = () => {
      zonesR.reload();
      devicesR.reload();
    };
    window.addEventListener(LIVE_ZONES_CHANGED, onBump);
    return () => window.removeEventListener(LIVE_ZONES_CHANGED, onBump);
  }, [armed, zonesR.reload, devicesR.reload]);

  const value = useMemo(() => {
    const devices = toDevices(devicesR.data);
    const byId = new Map(devices.map((d) => [d.id, d]));
    const labelOf = (id) => {
      const d = byId.get(String(id || "").trim());
      return d ? d.label : "";
    };
    const offline = devices.filter((d) => d.offline).map((d) => d.id);
    const alarms = toAlarms(alarmsR.data, labelOf);
    const zones = toZones(zonesR.data);
    const chip = (softR.data && softR.data.map_chip) || {};
    const laneIp = (softR.data && softR.data.lanes && softR.data.lanes.intent_prediction) || {};
    return {
      devices,
      people: toPeople(peopleR.data),
      /* Which parts of the roster the server could answer. A dark facet is
         reported rather than rendered as a plausible default. */
      peopleSources: (peopleR.data && peopleR.data.sources) || null,
      peopleReady: !!peopleR.data || !!peopleR.error,
      fences: zones.rings,
      zones: zones.list,
      zonesReady: !!zonesR.data || !!zonesR.error,
      offline,
      online: devices.length - offline.length,
      centre: centreOf(devices),
      labelOf,
      alarms,
      events: toEvents(eventsR.data),
      belief: toBelief(incidentsR.data, labelOf),
      ops: toOps(alarms),
      // No approval resource exists. The decision bar stays down rather than
      // inventing an order for an operator to approve.
      gate: null,
      // Bound-to-hardware is still a design invariant (always 0). Intent /
      // prediction counts come from GET /ai/soft-lanes when the session can.
      soft: softR.error
        ? { intents: UNKNOWN, plans: UNKNOWN, bound: 0 }
        : {
            intents: chip.intents != null ? chip.intents : (laneIp.intents != null ? laneIp.intents : UNKNOWN),
            plans: chip.predictions != null ? chip.predictions : (laneIp.predictions != null ? laneIp.predictions : UNKNOWN),
            bound: 0,
          },
      loading: alarmsR.loading || eventsR.loading || devicesR.loading,
      error: alarmsR.error || eventsR.error || incidentsR.error || devicesR.error || "",
      devicesReady: !!devicesR.data || !!devicesR.error,
      reload() {
        alarmsR.reload();
        eventsR.reload();
        incidentsR.reload();
        devicesR.reload();
        peopleR.reload();
        zonesR.reload();
        softR.reload();
      },
    };
  }, [alarmsR.data, alarmsR.loading, alarmsR.error,
      eventsR.data, eventsR.loading, eventsR.error,
      incidentsR.data, incidentsR.error,
      devicesR.data, devicesR.loading, devicesR.error,
      peopleR.data, peopleR.error,
      zonesR.data, zonesR.error,
      softR.data, softR.error]);

  return html`<${Ctx.Provider} value=${value}>${children}<//>`;
}

/** The live site state. Null outside a provider, so callers can fall back. */
export function useLive() {
  return useContext(Ctx);
}
